import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DriverDetailModal from "@/components/DriverDetailModal";

const empty = { firstname: "", lastname: "", phone: "", email: "", employee_number: "", active: true, category_id: "" };

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const [d, c] = await Promise.all([api.get("/drivers"), api.get("/categories")]);
      setDrivers(d.data);
      setCategories(c.data);
    } catch { /* ignore – handled globally */ }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...empty });
    setOpen(true);
  };
  const startEdit = (d) => { setEditing(d); setForm({ ...empty, ...d, category_id: d.category_id || "" }); setOpen(true); };

  const save = async () => {
    if (!form.firstname?.trim() || !form.lastname?.trim()) { toast.error("Bitte Vor- und Nachname angeben"); return; }
    if (!form.phone?.trim()) { toast.error("Bitte Telefonnummer angeben"); return; }
    if (!form.employee_number?.trim()) { toast.error("Bitte Personalnummer angeben"); return; }
    try {
      if (editing) {
        await api.patch(`/drivers/${editing.id}`, form);
        toast.success("Fahrer aktualisiert");
      } else {
        await api.post("/drivers", form);
        toast.success("Fahrer angelegt");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActive = async (d) => {
    try {
      await api.patch(`/drivers/${d.id}`, { active: !d.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = drivers.filter((d) => {
    const q = search.toLowerCase();
    return !q || `${d.firstname} ${d.lastname} ${d.email || ""} ${d.employee_number || ""}`.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl heading font-semibold">Fahrer</h1>
          <p className="text-sm text-muted-foreground">Fahrer anlegen und verwalten.</p>
        </div>
        <Button onClick={startCreate} data-testid="drivers-create-button">
          <Plus className="h-4 w-4 mr-1" /> Neuer Fahrer
        </Button>
      </div>

      <Card className="p-3">
        <div className="relative max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche nach Name, E-Mail, Personalnummer …"
            className="pl-9"
            data-testid="drivers-search-input"
          />
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2">Personalnr.</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Kategorie</th>
              <th className="text-left px-4 py-2">Telefon</th>
              <th className="text-left px-4 py-2">E-Mail</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} className="border-t border-border row-hover" data-testid={`driver-row-${d.id}`}>
                <td className="px-4 py-2 tabular-nums">{d.employee_number}</td>
                <td className="px-4 py-2">
                  <button onClick={() => setDetail(d)} className="text-blue-700 dark:text-blue-300 hover:underline font-medium" data-testid={`driver-name-link-${d.id}`}>
                    {d.firstname} {d.lastname}
                  </button>
                </td>
                <td className="px-4 py-2">
                  {d.category_name ? (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200" data-testid={`driver-category-${d.id}`}>{d.category_name}</span>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-2">{d.phone || "—"}</td>
                <td className="px-4 py-2">{d.email || "—"}</td>
                <td className="px-4 py-2">
                  <Switch checked={d.active} onCheckedChange={() => toggleActive(d)} data-testid={`driver-active-toggle-${d.id}`} />
                </td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(d)} data-testid={`driver-edit-${d.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground text-sm">Keine Fahrer gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Fahrer bearbeiten" : "Neuer Fahrer"}</DialogTitle>
            <DialogDescription>Stammdaten des Fahrers erfassen.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Vorname <span className="text-red-600">*</span></Label>
              <Input value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} data-testid="driver-firstname-input" />
            </div>
            <div>
              <Label>Nachname <span className="text-red-600">*</span></Label>
              <Input value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })} data-testid="driver-lastname-input" />
            </div>
            <div>
              <Label>Personalnummer <span className="text-red-600">*</span></Label>
              <Input value={form.employee_number} onChange={(e) => setForm({ ...form, employee_number: e.target.value })} data-testid="driver-empnum-input" />
            </div>
            <div>
              <Label>Telefon <span className="text-red-600">*</span></Label>
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="driver-phone-input" />
            </div>
            <div className="col-span-2">
              <Label>E-Mail <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="driver-email-input" />
            </div>
            <div className="col-span-2">
              <Label>Arbeitszeitmodell <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <Select value={form.category_id || "__none__"} onValueChange={(v) => setForm({ ...form, category_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="driver-category-select"><SelectValue placeholder="Kein ausgewähltes Modell" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Kein ausgewähltes Modell</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Optionale Vorlage – wird beim manuellen Anlegen einer Arbeitszeit zur Vorbefüllung genutzt.</p>
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="driver-active-input" />
              <span className="text-sm">Aktiv</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} data-testid="driver-save-button">{editing ? "Speichern" : "Anlegen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DriverDetailModal driver={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />
    </div>
  );
}
